import type { Key } from 'react-aria';
import { arrayMoveAfter, arrayMoveBefore } from '@gonfalon/collections/arrayMove';
import { useCreateShortcut } from '@gonfalon/rest-api/hooks/useCreateShortcut';
import { useDeleteShortcut } from '@gonfalon/rest-api/hooks/useDeleteShortcut';
import { useUpdateShortcutOrdering } from '@gonfalon/rest-api/hooks/useUpdateShortcutOrdering';
import { toastQueue } from '@launchpad-ui/components';
import { useSuspenseQuery } from '@tanstack/react-query';

import { getShortcutUIQuery } from './getShortcutUIQuery';
import type { Shortcut } from './types';

export const useShortcutData = ({ projectKey }: { projectKey: string }) => {
  const { data: shortcuts } = useSuspenseQuery(getShortcutUIQuery({ projectKey }));

  const { mutate: deleteShortcutMutation } = useDeleteShortcut();

  const { mutateAsync: createShortcutMutation } = useCreateShortcut();

  const { mutate: reorderShortcutsMutation } = useUpdateShortcutOrdering();

  const createShortcut = async (shortcut: Shortcut, options?: { onSuccess?: () => void }) => {
    try {
      await createShortcutMutation({
        body: {
          ...shortcut,
          context: {
            ...shortcut.context,
            environmentKeys: Array.from(shortcut.context.environmentKeys),
          },
        },
      });
    } catch {
      toastQueue.add({ title: 'An error occurred while creating the shortcut', status: 'error' });
      return;
    }
    options?.onSuccess?.();
  };

  const deleteShortcut = (shortcutKey: Key) => {
    deleteShortcutMutation(
      { projectKey, shortcutKey: shortcutKey as string },
      {
        onError() {
          toastQueue.add({ title: 'An error occurred while deleting the shortcut', status: 'error' });
        },
      },
    );
  };

  const reorderShortcut = (shortcutKey: Key, position: 'before' | 'after', otherKeys: Set<Key>) => {
    const nextShortcuts = shortcuts;
    if (nextShortcuts.length === 1) {
      return;
    }

    const target = nextShortcuts.find((it) => it.key === shortcutKey);
    const others: typeof shortcuts = [];
    for (const otherKey of otherKeys) {
      const other = nextShortcuts.find((it) => it.key === otherKey);
      if (other) {
        others.push(other);
      }
    }
    const nextList =
      position === 'after'
        ? arrayMoveAfter(nextShortcuts, others, target)
        : arrayMoveBefore(nextShortcuts, others, target);

    let didChange = false;
    for (const [index, item] of nextShortcuts.entries()) {
      if (item !== nextList[index]) {
        didChange = true;
        break;
      }
    }

    if (!didChange) {
      return;
    }

    const shortcutKeys = nextList.map((item) => item?.key).filter(Boolean) as string[];

    reorderShortcutsMutation(
      {
        projectKey,
        body: {
          shortcutKeys,
        },
      },
      {
        onError: () => {
          toastQueue.add({ title: 'An error occurred while reordering the shortcuts', status: 'error' });
        },
      },
    );
  };

  return {
    shortcuts,
    createShortcut,
    reorderShortcut,
    deleteShortcut,
  };
};
